import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { protectSheetWithRandomPassword } from "./ExcelUtils";

export const exportOpenAccountsExcelReport = async ({ data = [], startDate = null, endDate = null, loginPlaceId, filters = null }) => {
    try {
        if (!Array.isArray(data)) throw new Error("Invalid data format");

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Open Accounts");

        const LAST_COL = "G";

        sheet.columns = [
            { key: "accNumber",    width: 22 },
            { key: "productName",  width: 40 },
            { key: "createdDate",  width: 24 },
            { key: "createdByUser",width: 120 },
            { key: "branchName",   width: 26 },
            { key: "wasamName",    width: 26 },
            { key: "amount",       width: 18, style: { numFmt: "#,##0.00" } },
        ];

        const applyBorder = (cell) => {
            cell.border = {
                top:    { style: "thin" },
                left:   { style: "thin" },
                bottom: { style: "thin" },
                right:  { style: "thin" },
            };
        };

        const addMergedHeaderRow = (text, font, alignment, height) => {
            const row = sheet.addRow([text]);
            sheet.mergeCells(`A${row.number}:${LAST_COL}${row.number}`);
            const cell = sheet.getCell(`A${row.number}`);
            cell.value = text;
            cell.font = font;
            cell.alignment = alignment;
            if (height) row.height = height;
        };

        addMergedHeaderRow(
            "OPEN ACCOUNTS DATA REPORT",
            { bold: true, size: 16, color: { argb: "FF1F3A5F" } },
            { horizontal: "center", vertical: "middle" },
            30
        );

        if (startDate && endDate) {
            addMergedHeaderRow(
                `Period: ${startDate} to ${endDate}`,
                { bold: true, size: 12 },
                { horizontal: "center" }
            );
        }

        if (filters && Object.keys(filters).length > 0) {
            const filterLabels = {
                districts:   "District(s)",
                divisionals: "Divisional Area(s)",
                branches:    "Branch(es)",
                wasams:      "Wasam",
            };
            const filterText = Object.entries(filters)
                .filter(([, value]) => value)
                .map(([key, value]) => `${filterLabels[key] || key}: ${value}`)
                .join("   |   ");

            if (filterText) {
                addMergedHeaderRow(
                    `Filters -> ${filterText}`,
                    { bold: true, size: 10, color: { argb: "FF1F3A5F" } },
                    { horizontal: "center", wrapText: true }
                );
            }
        }

        addMergedHeaderRow(
            `Report Generated: ${dayjs().format("YYYY-MM-DD HH:mm:ss")}`,
            { italic: true, size: 9, color: { argb: "FF666666" } },
            { horizontal: "center" }
        );

        sheet.addRow([]);

        const headerRow = sheet.addRow([
            "Account Number",
            "Product Name",
            "Created Date",
            "Created By User",
            "Branch",
            "Wasam",
            "Amount (Rs.)",
        ]);
        const headerRowNum = headerRow.number;
        headerRow.height = 20;
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = {
                type:    "pattern",
                pattern: "solid",
                fgColor: { argb: "FF1F3A5F" },
            };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            applyBorder(cell);
        });

        const sortedData = [...data].sort((a, b) => {
            const aMember = (a.productName || "").toUpperCase().includes("MEMBER") ? 1 : 0;
            const bMember = (b.productName || "").toUpperCase().includes("MEMBER") ? 1 : 0;
            if (aMember !== bMember) return aMember - bMember;
            const byProduct = (a.productName || "").localeCompare(b.productName || "");
            if (byProduct !== 0) return byProduct;
            return new Date(b.createdDate) - new Date(a.createdDate);
        });

        if (data.length === 0) {
            const emptyRowNum = headerRowNum + 1;
            sheet.mergeCells(`A${emptyRowNum}:${LAST_COL}${emptyRowNum}`);
            const emptyCell = sheet.getCell(`A${emptyRowNum}`);
            emptyCell.value = "No records found for the selected criteria.";
            emptyCell.font  = { italic: true, color: { argb: "FF888888" } };
            emptyCell.alignment = { horizontal: "center" };
        } else {
            sortedData.forEach((row, idx) => {
                const dataRow = sheet.addRow([
                    row.accNumber     || "",
                    row.productName   || "",
                    row.createdDate   ? dayjs(row.createdDate).format("YYYY-MM-DD") : "",
                    row.createdByUser || "",
                    row.branchName    || "",
                    row.wasamName     || row.gnName || row.wasName || "",
                    row.amount != null ? Number(row.amount) : null,
                ]);
                dataRow.getCell(7).numFmt = "#,##0.00";
                dataRow.getCell(7).alignment = { horizontal: "right" };
                dataRow.eachCell((cell) => {
                    applyBorder(cell);
                    if (idx % 2 === 0) {
                        cell.fill = {
                            type:    "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFE8EFF8" },
                        };
                    }
                });
            });

            const totalRow = sheet.addRow([`Total Records: ${data.length}`]);
            sheet.mergeCells(`A${totalRow.number}:${LAST_COL}${totalRow.number}`);
            const totalCell = sheet.getCell(`A${totalRow.number}`);
            totalCell.font  = { bold: true };
            totalCell.fill  = {
                type:    "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD0D9E8" },
            };
            totalCell.border = { top: { style: "medium" } };
        }

        sheet.views = [{ state: "frozen", xSplit: 0, ySplit: headerRowNum - 1 }];

        sheet.autoFilter = {
            from: { row: headerRowNum, column: 1 },
            to:   { row: headerRowNum, column: 7 },
        };

        if (loginPlaceId === "1") {
            await protectSheetWithRandomPassword(sheet, 12);
        }

        const dateStr   = startDate ? startDate.replace(/-/g, "") : dayjs().format("YYYYMMDD");
        const fileName  = `Open_Accounts_Report_${dateStr}.xlsx`;
        const buffer    = await workbook.xlsx.writeBuffer();
        const blob      = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, fileName);

    } catch (err) {
        console.error("exportOpenAccountsExcelReport error:", err);
        throw err;
    }
};
